import type { PropType, Ref, Data } from "vue";
import {
  computed,
  defineComponent,
  getCurrentInstance,
  h,
  onMounted,
  toRef,
  ref,
  watch,
} from "vue";
import type {
  BmGridColumnConfiguration,
  BmGridConfiguration,
  BmGridState,
  GroupSummaryValueExtractor,
} from "@/components/controls/grid/BmGridData";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import { AgGridVue } from "@ag-grid-community/vue";
import type {
  CellClassParams,
  ColDef,
  GetQuickFilterTextParams,
} from "@ag-grid-community/core/dist/cjs/es5/entities/colDef";
import type {
  ColumnApi,
  ColumnState,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowGroupOpenedEvent,
  RowNode,
  SortChangedEvent,
} from "@ag-grid-community/core";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { pushRouteOrNewWindow } from "@/router/router";
import "@ag-grid-community/core/dist/styles/ag-grid.css";
import "@ag-grid-community/core/dist/styles/ag-theme-alpine.css";
import "@ag-grid-community/core/dist/styles/ag-theme-alpine-dark.css";
import "@/components/controls/grid/agGrid.sass";
import { BmGridCellRendererWrapper } from "@/components/controls/grid/BmGridCellRendererWrapper";
import { makeDeepRef } from "@/components/composables/componentTools";
import { isNotNullOrUndefined } from "@/app/apiutils";
import type {
  DragStoppedEvent,
  CellClickedEvent,
  FirstDataRenderedEvent,
  GridSizeChangedEvent,
  AgGridEvent,
  ColumnResizedEvent,
  RowDragMoveEvent,
  RowDragEndEvent,
  RowDragEnterEvent,
  RowDragLeaveEvent,
  SelectionChangedEvent,
} from "@ag-grid-community/core/dist/cjs/es5/events";
import { useMediaPrintingEvents } from "@/components/composables/mediaPrintingEvents";
import { isMacOs } from "@/app/ui/utilities";
import { indexedKeyComparer } from "@/components/controls/grid/BmGridComparers";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { GridDragDropEvent } from "@/components/controls/grid/BmGrid.vue";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { LicenseManager } from "@ag-grid-enterprise/core";
import environment from "@/environment";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { DetailCellRendererWrapper } from "@/components/controls/grid/DetailCellRendererWrapper";
import appStore from "@/store/modules/appStore";
import type { Location } from "vue-router/types/router";

LicenseManager.setLicenseKey(environment.agGridEnterpriseKey);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  MasterDetailModule,
]);

export interface AgGridInterface {
  getSortedAndFilteredRows: () => RowNode[];
}

export default defineComponent({
  name: "AgGrid",
  components: { BmGridCellRendererWrapper, DetailCellRendererWrapper },
  model: {
    prop: "selectedItemKeys",
    event: "update:selected-item-keys",
  },
  props: {
    items: { type: Array as PropType<any[]>, required: true },
    itemKey: {
      type: [String, Array, Function] as PropType<VuetifyPropertyArg>,
      required: true,
    },
    itemNoun: {
      type: String,
      required: false,
      default: () => "",
    },
    gridConfiguration: {
      type: Object as () => BmGridConfiguration,
      required: true,
    },
    gridState: {
      type: Object as () => BmGridState,
      required: true,
    },
    rowClickRoute: {
      type: [Function, Object] as PropType<
        ((item: any) => Location | null) | null
      >,
      default: null,
      required: false,
    },
    rowClasses: {
      type: Function as PropType<(row: any) => string[]>,
      default: (row: any) => [],
      required: false,
    },
    allowSelect: {
      type: Boolean,
      default: false,
    },
    selectedItemKeys: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    rowSelectable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => true,
    },
    allowDrag: {
      type: Boolean,
      default: false,
    },
    allowDrop: {
      type: Boolean,
      default: false,
    },
    rowDraggable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => true,
    },
    // The height of the grid when the number of rows exceed the predetermined
    // maximum count
    height: {
      type: [Number, String],
      required: false,
    },
    // The height of the grid, regardless of the number of rows
    overrideHeight: {
      type: [Number, String],
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultExpandGroups: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultExpandExpandable: {
      type: Boolean,
      required: false,
      default: false,
    },
    expandSlotRenderFn: {
      type: Function,
      required: false,
    },
    allowExpand: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowExpandable: {
      type: Function as PropType<(opts: { item: any }) => boolean>,
      required: false,
    },
    isFixedShapeReport: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeIds: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    getSortedAndFilteredRows() {
      let rowData: RowNode[] = [];
      if (this.gridApi) {
        this.gridApi.forEachNodeAfterFilterAndSort((node) => {
          rowData.push(node);
        });
      }
      return rowData;
    },
  },
  data: () => ({
    gridApi: null as GridApi | null,
  }),
  setup(props, context) {
    const agGridRef = ref<any>(null);
    const GRID_OVERFLOW_SPACER = 4;
    useMediaPrintingEvents(
      () => {
        setPrinterMode(true);
      },
      () => {
        setPrinterMode(false);
      }
    );
    const localApp: Ref<Data | null> = ref(null);
    onMounted(() => {
      // Get local app instance to expose functions
      // @TODO: [BREW-5793] use expose endpoint to expose data collection functions
      localApp.value = getCurrentInstance()?.proxy.$data ?? null;
    });

    // The number of rows displayed on first render of the grid (with data),
    // including grouped rows
    const initialDisplayedRowCount = ref<number | null>(null);

    const maxRowCountForAutoHeight = 15;

    const shouldUseFullHeight = computed(() => {
      const rowCount = initialDisplayedRowCount.value ?? props.items.length;
      return rowCount > maxRowCountForAutoHeight;
    });

    const gridApi = makeDeepRef<GridApi | null>(null);
    const gridColumnApi = makeDeepRef<ColumnApi | null>(null);

    const items = toRef(props, "items");
    const columnDefs = makeDeepRef<ColDef[]>([]);

    const selectedItemKeys = computed({
      get: () => props.selectedItemKeys,
      set: (keys: any[]) => context.emit("update:selected-item-keys", keys),
    });

    let dragStartedDataSnapshot: any[] = [];

    const internalRowSelectColId = "internalRowSelect";

    const updateAgGridStateFromBmGridState = (
      gridState: BmGridState = props.gridState
    ) => {
      if (gridApi.value && gridColumnApi.value) {
        // Set quick filter text
        gridApi.value.setQuickFilter(gridState.gridSearchFilter);

        //External Sorting
        const gridSortState = (gridApi.value.getColumnDefs() ?? [])
          .map((columnDef: ColDef) => {
            const isHidden = props.gridState.removedColumnKeys.includes(
              columnDef.colId!
            );
            if (columnDef.sortable) {
              let sortByIndex = props.gridState.sortBy.findIndex(
                (x) => x.columnKey === columnDef.colId
              );
              let sortItem = props.gridState.sortBy[sortByIndex];
              columnDef.sortable;
              if (sortItem) {
                return {
                  colId: columnDef.colId!,
                  sort: sortItem.descending ? "desc" : "asc",
                  sortIndex: sortByIndex,
                  hide: isHidden,
                } as ColumnState;
              }
            }
            return {
              colId: columnDef.colId!,
              sort: null,
              sortIndex: null,
              hide: isHidden,
            } as ColumnState;
          })
          .filter(isNotNullOrUndefined);

        // Sort our defs by our gridState order
        columnDefs.value.sort(
          indexedKeyComparer(
            props.gridState.columnOrder,
            (x: ColumnState) => x.colId
          )
        );

        gridColumnApi.value.applyColumnState({
          state: gridSortState,
          applyOrder: false,
        });

        gridApi.value.onFilterChanged();
      }
    };

    watch(
      () => [
        props.gridConfiguration.columns,
        props.gridConfiguration.summaries,
        props.gridConfiguration.defaultGridState,
        props.gridState,
      ],
      (_, oldValue) => {
        const columnKeysToGroupBy = new Set(
          props.gridConfiguration.defaultGridState?.groupBy ?? []
        );

        const columnKeyToAggregator: Record<
          string,
          GroupSummaryValueExtractor
        > = Object.fromEntries(
          props.gridConfiguration.summaries.map((summary) => [
            summary.columnKey,
            summary.groupSummaryValueExtractor,
          ])
        );

        let groupedCellRendererAssigned = false;

        const defs = Object.entries(props.gridConfiguration.columns).map(
          ([columnKey, value], index) => {
            const headerClass = `text-${value.align}`;

            const columnIsGroup = columnKeysToGroupBy.has(columnKey);

            let columnDef: ColDef = {
              colId: columnKey,
              headerName: value.headingText,
              valueGetter: (item) => {
                // Value is set by aggregator function if cell is in an
                // aggregated row
                if (!item?.data || item.node?.group) {
                  return undefined;
                }

                return value.extractor(item.data);
              },
              valueFormatter: (item) => {
                // If we're formatting an autoGroup column, prepend the
                // column heading
                if (item.colDef.rowGroup) {
                  return `${item.colDef.headerName}: ${item.value}`;
                }

                // Formatting is handled by BmGridCellRendererWrapper if cell is
                // in an aggregated row
                if (!item?.data || item.node?.group) {
                  return undefined;
                }

                return value.formatter(item.value, item.data);
              },
              sortable: props.allowSort && value.sortableAndFilterable,
              comparator: value.comparer,
              cellClass: (cellClassParams: CellClassParams) => {
                // Don't apply classes to grouped rows as they contain no data
                if (!cellClassParams.data || cellClassParams.node.group) {
                  return undefined;
                }

                const cellClasses = props.rowClasses({
                  item: cellClassParams.data,
                });

                cellClasses.push(value.classExtractor(cellClassParams.data));

                return cellClasses;
              },
              headerClass,
              rowGroup: columnIsGroup,
              // Permanently hide grouped columns (we currently don't support
              // reactively changing which grid columns to group by). If a column
              // isn't grouped, keep the default value of undefined, not false.
              // Grid column state will override this value depending on bm grid
              // state, but cannot if the value is set to false here.
              hide: columnIsGroup || undefined,
              suppressMovable: columnIsGroup,
            };

            /**
             * Recursively extract row keys of the given row node and all of
             * its parent row nodes
             */
            const extractAllParentRowKeys = (rowNode: RowNode): string[] => {
              if (!rowNode.parent || !rowNode.key) {
                return [];
              }

              return [...extractAllParentRowKeys(rowNode.parent), rowNode.key];
            };

            if (columnKeyToAggregator[columnKey]) {
              columnDef.aggFunc = (param) => {
                const aggregatorFunction = columnKeyToAggregator[columnKey];

                if (!props.isFixedShapeReport) {
                  return aggregatorFunction(param.values);
                }

                const parentGroupValues = extractAllParentRowKeys(
                  param.rowNode
                );

                const groupRowValue = parentGroupValues.pop() ?? "";

                return aggregatorFunction(
                  param.values,
                  groupRowValue,
                  parentGroupValues
                );
              };
            }

            if (value.rendererMetadata.actions) {
              columnDef.headerName = "";
              columnDef.lockPinned = true;
              columnDef.pinned = "right";
              columnDef.width = 36;
              columnDef.maxWidth = 36;
              columnDef.resizable = false;
              columnDef.suppressAutoSize = true;

              columnDef.cellClass = "no-padding";
              columnDef.cellStyle = {
                textAlign: "center",
              };
            }
            columnDef.cellStyle = {
              textAlign: value.align,
            };

            if (value.matcher.name === "dateMatcher") {
              columnDef.getQuickFilterText = (
                params: GetQuickFilterTextParams
              ): string =>
                params.value ? tenantService.formatDate(params.value) : "";
            }

            // Only use our custom cell renderer if we're not rendering a
            // grouped column. Otherwise use AG Grid's default grouped column
            // renderer
            if (!columnDef.rowGroup) {
              columnDef.cellRenderer = "BmGridCellRendererWrapper";

              columnDef.cellRendererParams = {
                context: {
                  value,
                  items: props.items,
                },
              };
            }

            value.rendererMetadata.isAgTruncated =
              !!value.rendererMetadata.textTruncationOptions;

            if (
              props.allowExpand &&
              !columnDef.rowGroup &&
              !groupedCellRendererAssigned &&
              !columnKeyToAggregator[columnKey] &&
              !props.gridState.removedColumnKeys.includes(columnDef.colId!)
            ) {
              groupedCellRendererAssigned = true;
              columnDef.cellRenderer = "agGroupCellRenderer";
              columnDef.cellRendererParams = {
                innerRenderer: "BmGridCellRendererWrapper",
                innerRendererParams: {
                  context: {
                    value,
                    items: props.items,
                  },
                },
              };
            }

            return columnDef;
          }
        );

        if (props.allowSelect) {
          defs.unshift({
            colId: internalRowSelectColId,
            headerName: "",
            width: 36,
            maxWidth: 36,
            lockPinned: true,
            resizable: false,
            suppressAutoSize: true,
            cellStyle: {
              textAlign: "center",
              paddingLeft: "8px",
            },
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerClass: "checkbox",
            showDisabledCheckboxes: true,
            headerCheckboxSelectionFilteredOnly: true,
            lockPosition: "left",
          });
        }

        if (props.allowDrag) {
          defs.unshift({
            colId: "internalDragHandle",
            headerName: "",
            width: 36,
            maxWidth: 36,
            lockPinned: true,
            pinned: "left",
            resizable: false,
            suppressAutoSize: true,
            cellStyle: {
              textAlign: "center",
              paddingLeft: "8px",
            },
            rowDrag: true,
          });
        }

        columnDefs.value = defs;
        updateAgGridStateFromBmGridState();
      },
      { immediate: true, deep: true }
    );

    watch(() => props.gridState, updateAgGridStateFromBmGridState, {
      deep: true,
    });

    const columnMap = computed(() => {
      const columns = gridColumnApi.value?.getColumns() ?? [];

      // get all configurations for the visible columns
      return columns.reduce((acc, column) => {
        const colId = column.getColId();
        const columnConfiguration = props.gridConfiguration.columns[colId];
        if (columnConfiguration) {
          acc.set(colId, columnConfiguration);
        }
        return acc;
      }, new Map<string, BmGridColumnConfiguration>());
    });

    const doesExternalFilterPass = (node: RowNode) => {
      // get all filters from gridState
      const filters = props.gridState.columnFilters;

      for (const filterColumnId of Object.keys(filters)) {
        const columnConfiguration = columnMap.value.get(filterColumnId);
        if (columnConfiguration) {
          const filterFunction = columnConfiguration.columnFilter?.buildApply(
            filters[filterColumnId]
          );
          if (filterFunction) {
            if (
              !filterFunction(
                columnConfiguration.extractor(node.data),
                columnConfiguration.extractAndFormat(node.data)
              )
            ) {
              return false;
            }
          }
        }
      }
      // if no filters are set, return true
      // if all filters pass, return true
      return true;
    };

    /**
     * Intelligently resizes ag grid columns.
     *
     * 'autoSizeAllColumns' achieves 90% of the goal using 'excel double click' widths. However if the content is too
     * small for the width then it creates a slightly ugly gap after the last column to our pinned action column.
     *
     * This code stretches the last column to fill the remaining space in the grid.
     *
     * TODO: this doesn't work nicely with row grouping enabled
     */
    const resizeColumns = (params: AgGridEvent<any>) => {
      let refreshColumnDefs = false;
      // Applies auto sizing to all columns which sizes using "excel double click" logic on the visible rows.
      params.columnApi.autoSizeAllColumns();

      const clientWidth = agGridRef.value?.$el.clientWidth;
      // if we know the full width then we look to fill remaining space.
      if (!clientWidth) {
        return;
      }

      const displayedColumns = params.columnApi.getAllDisplayedColumns();

      if (displayedColumns.length <= 1) {
        return;
      }

      const colDefs =
        (params.api.getColumnDefs() as ColDef[] | undefined) ?? [];

      // Set all of our saved widths before all of our calculations
      colDefs.forEach((colDef) => {
        if (!colDef.colId) {
          return;
        }

        const savedWidth = props.gridState.columnPixelWidthMap.get(
          colDef.colId
        );

        if (savedWidth === undefined) {
          return;
        }

        if (colDef.width !== savedWidth) {
          colDef.width = savedWidth;
          refreshColumnDefs = true;
        }
      });

      let totalRenderedWidths = displayedColumns?.reduce(
        (sum, col) => (sum += col.getActualWidth()),
        0
      );

      const columnDefMap = displayedColumns.reduce((acc, col) => {
        const colDef = colDefs.find((x) => x.colId === col.getColId());
        if (colDef) {
          acc.set(col.getColId(), colDef);
        }
        return acc;
      }, new Map<string, ColDef>());

      // AdjustableColumnsCount is the number of columns we can adjust the width of minus any pinned columns.
      // Note: internal row select is "pinned" but we don't set it as pinned in the column definition (for styling
      // reasons), so exclude it from the adjustableColumnsCount too
      const adjustableColumnsCount = displayedColumns.filter(
        (x) => !x.isPinned() && x.getColId() !== internalRowSelectColId
      ).length;

      // if overflow then remove space from truncated columns

      if (clientWidth < totalRenderedWidths + GRID_OVERFLOW_SPACER) {
        const truncatedCols = displayedColumns.filter((col) => {
          const colDef = columnDefMap.get(col.getColId());
          return colDef ? isAgTruncated(colDef) : false;
        });
        if (truncatedCols.length) {
          const truncationInitialMinSize = 250; // initial min size for all truncatable columns
          const truncationInitialMaxSize =
            (clientWidth / (displayedColumns.length ?? 1)) * 2; // initial max size is the min size multiplied by 2

          const delta =
            clientWidth - totalRenderedWidths - GRID_OVERFLOW_SPACER;
          const deltaPerColumn = Math.floor(delta / truncatedCols.length);

          // Adjust the width of the truncated columns
          truncatedCols.forEach((col) => {
            // Ignore truncation if we're a saved width
            if (props.gridState.columnPixelWidthMap.get(col.getColId())) {
              return;
            }
            const colWidth = col.getActualWidth();
            let newColWidth = colWidth + deltaPerColumn;

            if (newColWidth < truncationInitialMinSize) {
              newColWidth = truncationInitialMinSize;
            } else if (newColWidth > truncationInitialMaxSize) {
              newColWidth = truncationInitialMaxSize;
            }

            const colDef = columnDefMap.get(col.getColId());
            if (colDef) {
              colDef.width = newColWidth;
              totalRenderedWidths += newColWidth - colWidth;
            }
          });
          refreshColumnDefs = true;
        }
      }

      // Is there currently a visible gap
      if (clientWidth > totalRenderedWidths) {
        // is there a pinned right column?
        const delta = clientWidth - totalRenderedWidths - GRID_OVERFLOW_SPACER;

        // apply delta to all columns evenly
        let deltaPerColumn = Math.floor(delta / adjustableColumnsCount);
        let remainder = delta % adjustableColumnsCount;
        displayedColumns.forEach((col, index) => {
          const colDef = columnDefMap.get(col.getColId());

          // Ignore resizing if we're a saved width
          if (props.gridState.columnPixelWidthMap.get(col.getColId())) {
            return;
          }

          // if not actions column and is displayed then apply delta
          if (colDef && colDef.width && !colDef.pinned) {
            if (index === adjustableColumnsCount - 1) {
              // bump up the last row with any remaining delta
              colDef.width = colDef.width + remainder + deltaPerColumn;
            } else {
              colDef.width = colDef.width + deltaPerColumn;
            }
          }
        });
        refreshColumnDefs = true;
      }

      if (refreshColumnDefs) {
        params.api.setColumnDefs(colDefs); // A full def set is needed to re-layout the columns
      }
    };

    const getFilteredRowData = () => {
      let rowData: any[] = [];
      if (gridApi.value) {
        gridApi.value.forEachNodeAfterFilter((node) => rowData.push(node.data));
      }
      return rowData;
    };

    const onRowDragEnter = (event: RowDragEnterEvent) => {
      // Fired on dragStart and reEnter
      // Take a snapshot of the data before the drag starts
      if (dragStartedDataSnapshot.length === 0) {
        dragStartedDataSnapshot = [...getFilteredRowData()];
      }
    };

    const onRowDragLeave = (event: RowDragLeaveEvent) => {
      // Drag left the grid, reset state as we don't know if the drag will end or not
      gridApi.value?.setRowData(dragStartedDataSnapshot);
      dragStartedDataSnapshot = [];
    };

    const onRowDragEnd = (event: RowDragEndEvent) => {
      // Drag ended - Not fired if dropped outside of grid

      const draggedRowKey = getRowKey(event.node.data);
      const fromIndex = dragStartedDataSnapshot.findIndex(
        (x) => getRowKey(x) === draggedRowKey
      );

      if (fromIndex < 0 || fromIndex === event.overIndex) {
        return;
      }
      // Get the index of the item to drop under. Adjust for the fact that the dragged item has been removed from the list
      const toIndex = event.overIndex - (event.overIndex > fromIndex ? 0 : 1);

      context.emit("drag-drop", {
        draggedRowKeys: [draggedRowKey],
        droppedOnRowKey: getRowKey(dragStartedDataSnapshot[toIndex]),
      } as GridDragDropEvent);

      dragStartedDataSnapshot = [];
    };

    const onRowDragMove = (event: RowDragMoveEvent) => {
      // Drag moving (only on the current grid)
      const movingNode = event.node;
      const overNode = event.overNode;
      const rowNeedsToMove = movingNode !== overNode;

      if (
        rowNeedsToMove &&
        event.overIndex !== undefined &&
        dragStartedDataSnapshot.length
      ) {
        const gridItems = [...dragStartedDataSnapshot];

        const fromIndex = gridItems.indexOf(movingNode.data);
        const toIndex = event.overIndex;

        // Move item in array for ag-grid to pick up and show movement
        const element = gridItems[fromIndex];
        gridItems.splice(fromIndex, 1);
        gridItems.splice(toIndex, 0, element);

        gridApi.value?.setRowData(gridItems);
        gridApi.value?.clearFocusedCell();
      }
    };

    watch(shouldUseFullHeight, () => {
      if (gridApi.value) {
        if (shouldUseFullHeight.value) {
          gridApi.value.setDomLayout("normal");
        } else {
          gridApi.value.setDomLayout("autoHeight");
        }
      }
    });

    const setPrinterMode = (printerMode: boolean) => {
      if (gridApi.value) {
        gridApi.value.setDomLayout(printerMode ? "print" : "normal");
      }
    };

    const updateFilterSummary = (displayedRowCount?: number) => {
      if (gridApi.value) {
        context.emit(
          "filter-summary",
          `${displayedRowCount ?? gridApi.value.getDisplayedRowCount()} / ${
            props.items.length
          } ${props.itemNoun}`
        );
      }
    };

    /**
     * Expand or collapse all grouped rows and master detail rows (expandable
     * rows).
     *
     * Note: there may be a noticeable performance hit if async calls are made
     * on row expansion.
     */
    const applyExpandCollapseAll = (opts: {
      expand: boolean;
      onlyExpandGroups?: boolean;
    }) => {
      if (opts.onlyExpandGroups) {
        opts.expand ? gridApi.value?.expandAll() : gridApi.value?.collapseAll();
      } else {
        gridApi.value?.forEachNode((node) => {
          node.setExpanded(opts.expand);
        });
      }
    };

    watch(
      () => props.defaultExpandGroups,
      (defaultExpandGroups) => {
        applyExpandCollapseAll({
          expand: defaultExpandGroups,
          onlyExpandGroups: true,
        });
      }
    );

    watch(
      () => props.defaultExpandExpandable,
      (defaultExpandExpandable) => {
        applyExpandCollapseAll({
          expand: defaultExpandExpandable,
        });
      }
    );

    const getRowId = (params: GetRowIdParams | RowNode) => {
      return getRowKey(params.data);
    };

    const getRowKey = (rowData: any) =>
      getPropertyFromItem(rowData, props.itemKey);

    watch(
      () => props.isLoading,
      () => {
        if (props.isLoading) {
          gridApi.value?.showLoadingOverlay();
        } else {
          gridApi.value?.hideOverlay();
        }
      }
    );

    const isAgTruncated = (column: ColDef) =>
      column.cellRendererParams?.context?.value?.rendererMetadata
        ?.isAgTruncated ?? false;

    const gridHeight = computed(
      () =>
        props.overrideHeight ||
        (shouldUseFullHeight.value ? props.height || "70vh" : "")
    );

    return () => {
      const propArgs: GridOptions = {
        rowData: props.items,
        defaultColDef: {
          resizable: true,
        },
        columnDefs: columnDefs.value,
        headerHeight: 36,
        doesExternalFilterPass,
        isExternalFilterPresent: () => true,
        suppressDragLeaveHidesColumns: true,
        suppressScrollOnNewData: true,
        alwaysShowHorizontalScroll: true,
        maintainColumnOrder: false,
        enableCellTextSelection: true,
        animateRows: true,
        // Some of our grids have had non-unique row Ids in the past, and these break when provided to AgGrid
        // so only provide row ids if requested to do so.
        getRowId: props.includeIds ? getRowId : undefined,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        isRowSelectable: (node) => props.rowSelectable({ item: node.data }), // @TODO: This matches our current grids layout, will need to be updated

        // fix for scrollbar overlapping / not easily draggable on Mac OS
        // https://github.com/ag-grid/ag-grid/issues/2962#issuecomment-674006902
        scrollbarWidth: isMacOs.value ? 15 : undefined,
        autoGroupColumnDef: {
          cellRendererParams: { suppressCount: true },
        },
        suppressAggFuncInHeader: true,
        masterDetail: props.allowExpand,
        detailCellRenderer: "DetailCellRendererWrapper",
        detailCellRendererParams: {
          renderFn: props.expandSlotRenderFn,
        },
        detailRowAutoHeight: true,
        isRowMaster: (rowData) => {
          return props.rowExpandable?.({ item: rowData }) ?? false;
        },
        groupDefaultExpanded: 0,
      };

      let onArgs: { [key: string]: Function | Function[] } = {};

      onArgs.cellClicked = async (event: CellClickedEvent) => {
        if (window.getSelection()?.type === "Range") {
          // text has been selected, so do not perform the click action.
          // Useful if the user is trying to select text out of the grid.
          // https://stackoverflow.com/questions/63453752/ag-grid-how-disable-cell-click-event-when-enablecelltextselection-is-used-and-t
          return;
        }

        if (event.colDef.colId === internalRowSelectColId) {
          // Toggle row selected state if the cell is selected so
          // the user doesn't have to click directly on the checkbox
          event.node.setSelected(!event.node.isSelected());
          return;
        }

        if (event.node.group) {
          // Prevent trying to evaluate rowClickRoute if a grouped row is
          // clicked (a grouped row has no data)
          return;
        }

        if (event.colDef.cellRenderer === "agGroupCellRenderer") {
          // Prevent trying to evaluate rowClickRoute if an expandable
          // cell is clicked
          return;
        }

        // Inner renderer params are used when BmGridCellRendererWrapper is
        // wrapped by a DetailCellRendererWrapper (cell that allows master
        // detail row to be expanded)
        const cellRendererParamsContext =
          event.colDef.cellRendererParams?.context ??
          event.colDef.cellRendererParams?.innerRendererParams.context;

        if (props.rowClickRoute) {
          const isEmailAddressCell =
            !!cellRendererParamsContext?.value.rendererMetadata.emailExtractor;

          const isPhoneNumberCell =
            !!cellRendererParamsContext?.value.rendererMetadata.phoneExtractor;

          const rowHasLocation =
            !!cellRendererParamsContext?.value.rendererMetadata
              .locationExtractor;

          const rowHasExternalLink =
            !!cellRendererParamsContext?.value.rendererMetadata
              .externalLinkExtractor;

          if (
            isEmailAddressCell ||
            isPhoneNumberCell ||
            rowHasLocation ||
            rowHasExternalLink
          ) {
            return;
          }

          const route = props.rowClickRoute(event.data);

          if (!route) {
            return;
          }

          await pushRouteOrNewWindow(route, event.event as any);
          return;
        }

        context.emit("row-click", event.data);
      };

      onArgs.dragStopped = (e: DragStoppedEvent) => {
        const newColumnOrder = e.columnApi.getColumnState().map((c) => c.colId);
        props.gridState.applyColumnOrder(newColumnOrder);
      };

      onArgs.gridReady = async (params: GridReadyEvent) => {
        gridApi.value = params.api;
        gridColumnApi.value = params.columnApi;

        // Force update of current grid state
        updateAgGridStateFromBmGridState(props.gridState);
        if (localApp.value) {
          localApp.value.gridApi = gridApi.value;
        }
      };

      // AGGrid recommends performing column resizing after some data has been loaded.
      onArgs.firstDataRendered = (params: FirstDataRenderedEvent) => {
        initialDisplayedRowCount.value = params.api.getDisplayedRowCount();

        // ensure filter summary has correct row count on first load
        updateFilterSummary(initialDisplayedRowCount.value);
        resizeColumns(params);

        // set checkboxes as selected if the row key is selected
        params.api.forEachNode((node) => {
          if (props.selectedItemKeys.length) {
            node.setSelected(
              props.selectedItemKeys.includes(getRowKey(node.data))
            );
          }
        });

        applyExpandCollapseAll({
          expand: props.defaultExpandGroups || props.defaultExpandExpandable,
          onlyExpandGroups:
            props.defaultExpandGroups && !props.defaultExpandExpandable,
        });
      };

      onArgs.gridSizeChanged = (params: GridSizeChangedEvent) => {
        // @FIXME: [BREW-4824] Causes updates when grid overflows for the first time leading to grid trying to fit but unpredictable results
        resizeColumns(params);
      };

      onArgs.sortChanged = (params: SortChangedEvent) => {
        const agSortStatus = (params.columnApi?.getColumns() ?? [])
          .filter((column) => column.getSort())
          // sort by sort index asc desc or null
          .sort((a, b) => {
            if (a.getSortIndex() && b.getSortIndex()) {
              return 0;
            } else if (a.getSortIndex()) {
              return 1;
            } else if (b.getSortIndex()) {
              return -1;
            } else {
              return a.getSortIndex()! - b.getSortIndex()!;
            }
          })
          .map((column) => {
            const sort = column.getSort();
            return {
              columnKey: column.getColId(),
              descending: sort === "desc",
            };
          });

        // don't allow drag drop if we're sorting
        gridApi.value?.setSuppressRowDrag(agSortStatus.length > 0);
        props.gridState.applyExternalSorting(agSortStatus);
      };

      onArgs.filterChanged = () => {
        updateFilterSummary();
      };

      onArgs.rowDataUpdated = () => {
        updateFilterSummary();
      };

      onArgs.columnResized = (e: ColumnResizedEvent) => {
        // prevent persisting default auto-sized column widths if the
        // grid isn't visible
        if (!e.column || !agGridRef.value?.$el.clientWidth) {
          return;
        }

        props.gridState.setColumnPixelWidth(
          e.column.getColId(),
          e.column.getActualWidth()
        );
      };

      watch(selectedItemKeys, (newKeys, oldKeys) => {
        if (!newKeys || !oldKeys) {
          return;
        }

        // if deselecting all keys, ensure that ag grid performs deselection
        if (oldKeys.length > 0 && newKeys.length === 0) {
          gridApi.value?.deselectAll();
        }
      });

      onArgs.selectionChanged = (e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        selectedItemKeys.value = selectedRows.map((row) => getRowKey(row));
      };

      onArgs.rowDragMove = onRowDragMove;
      onArgs.rowDragEnd = onRowDragEnd;
      onArgs.rowDragEnter = onRowDragEnter;
      onArgs.rowDragLeave = onRowDragLeave;

      onArgs.rowGroupOpened = (event: RowGroupOpenedEvent) => {
        if (event.data) {
          context.emit("expand-row", { item: event.data });
        }
      };

      return h(AgGridVue, {
        class:
          appStore.state.theme === "dark"
            ? "ag-theme-alpine-dark"
            : "ag-theme-alpine",
        style: {
          width: "100%",
          height: gridHeight.value, // Allow AG grid to size automatically when not clipped
        },
        props: propArgs,
        on: onArgs, // Remove "On" Prefix from all events
        ref: agGridRef,
      });
    };
  },
});
